/** @jsx jsx */
import React, { useState, createContext, useEffect } from "react";
import { css, jsx } from "@emotion/core";
import BlockUI from "components/molecules/BlockUi";
import FlexBox from "components/atoms/FlexBox";
import moment from "moment";
import FormLabel from "components/atoms/Form/FormLabel";
import FormContents from "components/atoms/Form/FormContents";
import { grayScale } from "components/styles";
import TabControl from "components/atoms/TabControl";
import BarcodeRecorder from "./BarcodeRecorder";
import ICCardRecorder from "./ICCardRecorder";
import ICCardRecorderNotAll from "./ICCardRecorderNotAll";
import FaceRecorder from "./FaceRecorder";
import DataTable from "components/organismos/DataTable/DataTable";
import useRecorder from "./hook";
import { Column } from "react-table";
import { utilityColor } from "components/styles";
import ReactTooltip from "react-tooltip";
import BodyText from "components/atoms/BodyText";
import { OptionType } from "components/atoms/Select";
import ImageModel from "../ImageModel";
import { useHistory } from "react-router-dom";
import Icon from "components/atoms/Icon";
import TextTooltip from "components/molecules/TextTooltip";

const isIpad = window.matchMedia("(max-width: 1024px)").matches;

type SelectType = "id" | "face" | "gps" | "icCard";

const SelectType: { [key in SelectType]: SelectType } = {
  id: "id",
  face: "face",
  gps: "gps",
  icCard: "icCard",
};

const styles = {
  backHistory: css(
    {
      position: 'relative',
      top: '24px',
      left: '-65px'
    },
    css`&:hover svg {
      opacity:0.7
    }`
  ),
  container: css({
    maxWidth: "1140px",
    margin: "auto",
    width: "calc(100% - 30px)"
  }),
  labelForm: css({
    marginBottom: "20px",
    color: grayScale.gray01,
    fontSize: "18px"
  }),
  shopName: css({
    paddingTop: "12px",
    marginRight: isIpad ? "20px" : "150px",
    fontSize: "18px",
    color: "black"
  })
};

const tabControl = css`
  display: flex;
  justify-content: space-between;
  margin-left: ${isIpad ? '0' : '100px'};
  div {
    width: 290px;
  }
  label {
    border: 0;
  }
  label:hover {
    background: #e7f1fd;
    border-radius: 10px;
    border: 0;
  }
`;

const dataTable = css`
  margin-top: 24px;
  padding-bottom: 0px;
  table tbody td,
  table thead th {
    text-align: center;
  }
  table thead th {
    font-weight: bold;
  }
  table tr td,
  table tr th {
    &:first-of-type {
      border-left: 0 !important;
    }
    &:last-of-type {
      border-right: 0 !important;
    }
  }
  table th:nth-of-type(1) {
    width: 110px;
  }
  table th:nth-of-type(2) {
    width: 90px;
  }
  table th:nth-of-type(3) {
    width: 90px;
  }
  table th:nth-of-type(4) {
    width: 80px;
  }
  table th:nth-of-type(5) {
    width: 80px;
  }
  table th:nth-of-type(6) {
    width: 65px;
  }
  table th:nth-of-type(7) {
    width: 80px;
  }
`;

// const cellImage = css`
//   div {
//     height: 55px;
//     width: 55px;
//     margin-right: 8px;
//   }
//   img {
//     border-radius: 50%;
//   }
// `;

//Context global variable
type contextType = {
  companyCode: string;
  shopCode: string;
  stampByBelongOrg: number;
};

export const TimeRecorderContext = createContext<contextType>({} as contextType);

const Recorder: React.FC = () => {
  const history = useHistory();
  const [selectType, setSelectType] = React.useState<SelectType>(SelectType.id);
  const [items, setItems] = useState<Array<OptionType>>();

  const columns: Array<Column<any>> = React.useMemo(() => [
    {
      Header: () => "氏名",
      accessor: "staffName",
      id: "staffName",
      Cell: (cell: {
        row: { isExpanded: boolean; original: any };
        page: [];
      }) => {
        const [imageOpen, setImageOpen] = useState(false);
        const { staffCode, staffName, stampTime, stampName } = cell.row.original;
        const imageURL = cell.row.original.photoFullpath;

        return (
          <FlexBox>
            <div>
              {
                imageURL && (
                  <React.Fragment>
                    <div 
                      style={{
                        textAlign: 'center', 
                        display: 'flex', 
                        alignItems: 'center', 
                        overflowWrap: 'anywhere'
                      }}
                    >
                      <img 
                        src={imageURL} 
                        alt={imageURL} 
                        width={55} 
                        height={55}
                        style={{ cursor: 'pointer', borderRadius: '50%', marginRight: '15px' }}
                        onClick={() => setImageOpen(true)} 
                      />
                      <ImageModel
                        open={imageOpen}
                        closeHandler={() => { setImageOpen(false) }}
                        imageURL={imageURL}
                        descript={
                          staffCode + '/' + 
                          staffName + '/' + 
                          stampName + '/' + 
                          moment(stampTime).format('YYYY/MM/DD HH:mm')
                        }
                      />
                    </div>
                  </React.Fragment>
                )
              }
            </div>
            {
              staffName.length >= 20 
              ? <TextTooltip 
                  text={staffName} 
                  textDis={(
                    <div 
                      className="ellipsis headline-cell-width-120" 
                      style={{
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        width: `${imageURL !== null ? '70px' : '140px'}`
                      }}
                    >
                      <BodyText customStyle={css({marginLeft: '27px'})}>
                        {cell.row.original.staffName}
                      </BodyText>
                    </div>
                  )}
                /> 
              : <BodyText customStyle={css({marginLeft: '27px'})}>
                  {cell.row.original.staffName}
                </BodyText>
            }
          </FlexBox>
        );
      }
    },
    {
      Header: '組織コード',
      accessor: 'orgCode'
    },
    {
      Header: '組織名',
      accessor: 'orgName'
    },
    {
      Header: "スタッフコード",
      accessor: "staffCode"
    },
    {
      Header: '打刻方法',
      accessor: 'stampModeName',
      id: 'stampModeName',
      Cell: (cell: { row: { isExpanded: boolean, original: any }, page: [] }) => {
        return (
          <React.Fragment>
            <div style={{width: '100%'}}>{cell.row.original.stampModeName}</div>
            {(
              cell.row.original.errorMessage !== null &&
              cell.row.original.errorMessage !== ""
            ) && (
              <BodyText customStyle={css({fontSize: '13px', color: '#333333'})}>
                {cell.row.original.cardId}
              </BodyText>
            )}
          </React.Fragment>
        );
      }
    },
    {
      Header: () => "打刻種別",
      accessor: "errorMessage",
      id: "errorMessage",
      Cell: (cell: {
        row: { isExpanded: boolean; original: any };
        page: [];
      }) => {
        const { stampName, isRemote } = cell.row.original;

        if (
          cell.row.original.errorMessage === null ||
          cell.row.original.errorMessage === ""
        ) {
          return (
            <div>
              {
                stampName === '出勤' 
                ? (
                    isRemote 
                    ? stampName + ' (テレワーク)' 
                    : stampName + ' (オフィス)'
                  ) 
                : stampName
              }
            </div>
          );
        }

        if (
          cell.row.original.errorMessage !== null &&
          cell.row.original.errorMessage !== ""
        ) {
          return (
            <span>
              <div
                css={css({ color: utilityColor.note })}
                data-tip={cell.row.original.errorMessage}
              >
                {stampName === '出勤' ? (isRemote ? stampName + `${companyCode === 'HPo6wkyuze' ? ' (在宅)' : ' (テレワーク)'}` : stampName + ' (オフィス)') : stampName}
              </div>
              <ReactTooltip html={true} />
            </span>
          );
        }
      }
    },
    {
      Header: () => "打刻時刻",
      accessor: "stampTime",
      id: "stampTime",
      Cell: (cell: {
        row: { isExpanded: boolean; original: any };
        page: [];
      }) =>
        cell.row.original.stampTime
        ? moment(cell.row.original.stampTime).format("YYYY/MM/DD HH:mm:ss")
        : "",
    }
  ], []);

  const [isLoading, setIsLoading] = useState(false);

  const {
    stampHistoryList,
    currentDay,
    timeNow,
    second,
    stampOrgCode,
    stampOrgName,
    loginOrgCode,
    loginOrgName,
    setStampOrgCode,
    setStampOrgName,
    setLoginOrgCode,
    setLoginOrgName,
    resultCurrentTime,
    useFaceStamp,
    useBarcodeStamp,
    useICCardStamp,
    disabledRecorder,
    fetchHistoryData,
    companyCode,
    specifyCode
  } = useRecorder(setIsLoading);

  const dataTimes = {
    timeNow,
    currentDay,
    second,
    resultCurrentTime,
  };

  const dataSettings = {
    stampOrgCode,
    setStampOrgCode,
    stampOrgName,
    setStampOrgName,
    loginOrgCode,
    setLoginOrgCode,
    loginOrgName,
    setLoginOrgName,
    useFaceStamp,
    useBarcodeStamp,
    useICCardStamp
  };

  useEffect(() => {
    if((useICCardStamp === null || useICCardStamp === null || useFaceStamp === null) && specifyCode !== "") return;

    const itemList: any = [];
    
    if (useICCardStamp || specifyCode === "") {
      itemList.push(
        {
          value: SelectType.icCard,
          display: "ICカード",
        },
      );
    }

    if (useBarcodeStamp) {
      itemList.push(
        {
          value: SelectType.id,
          display: "ID",
        }
      );
    }

    if (useFaceStamp) {
      itemList.push(
        {
          value: SelectType.face,
          display: "顔認証",
        }
      );
    }

    setItems(itemList);
    setSelectType(itemList[0]?.value);
  }, [useBarcodeStamp, useFaceStamp, useICCardStamp, specifyCode]);

  return (
    <TimeRecorderContext.Provider
      value={{
        companyCode: companyCode,
        shopCode: stampOrgCode,
        stampByBelongOrg: 0
      }}
    >
      <BlockUI blocking={isLoading}>
        <div css={styles.container}>
          <div 
            css={styles.backHistory} 
            onClick={() => history.push("/login")}
          >
            <Icon type="back" color="#666" />
          </div>
          <div css={styles.labelForm}>打刻</div>
          {
            (useBarcodeStamp !== 0 || useFaceStamp !== 0 || useICCardStamp !== 0 || specifyCode === "" ) ? (
              <FormContents customStyle={css({paddingTop: '15px'})}>
                <div css={tabControl}>
                  {
                    items && (
                      <TabControl
                        styleActive={{ backgroundColor: "#E7F1FD", borderRadius: "10px" }}
                        items={items}
                        setValue={setSelectType as (arg: string) => void}
                        currentValue={selectType}
                      ></TabControl>
                    )
                  }
                  <BodyText customStyle={styles.shopName}>{stampOrgName}</BodyText>
                </div>
                {
                  (selectType === SelectType.icCard && specifyCode === "") ? (
                    <ICCardRecorder
                      dataTimes={dataTimes}
                      dataSettings={dataSettings}
                      setIsLoading={setIsLoading}
                      disabledRecorder={disabledRecorder}
                      fetchHistoryData={fetchHistoryData}
                    />
                  ) : ''
                }
                {
                  (selectType === SelectType.icCard && specifyCode !== "") ? (
                    <ICCardRecorderNotAll
                      dataTimes={dataTimes}
                      dataSettings={dataSettings}
                      setIsLoading={setIsLoading}
                      disabledRecorder={disabledRecorder}
                      fetchHistoryData={fetchHistoryData}
                    />
                  ) : ''
                }
                {
                  (useBarcodeStamp === 1 && selectType === SelectType.id) ? (
                    <BarcodeRecorder
                      dataTimes={dataTimes}
                      dataSettings={dataSettings}
                      setIsLoading={setIsLoading}
                      disabledRecorder={disabledRecorder}
                      fetchHistoryData={fetchHistoryData}
                    />
                  ) : ''
                }
                {
                  (useFaceStamp === 1 && selectType === SelectType.face) ? (
                    <FaceRecorder
                      dataTimes={dataTimes}
                      dataSettings={dataSettings}
                      setIsLoading={setIsLoading}
                      disabledRecorder={disabledRecorder}
                      fetchHistoryData={fetchHistoryData}
                    />
                  ) : ''
                }
                {/* {selectType === SelectType.gps && (
                <GPSRecorder
                  dataTimes = {dataTimes}
                  dataSettings= {dataSettings}
                  setIsLoading={setIsLoading}
                  fetchHistoryData={fetchHistoryData}
                />
              )} */}
              </FormContents>
            ) : ''
          }

          <div css={dataTable}>
            <FormContents>
              <div css={css({ marginLeft: "15px" })}>
                <FormLabel
                  label="打刻履歴"
                  customStyle={css({ fontWeight: "bold", marginTop: "20px" })}
                />
              </div>
              <div className='table-bordered table-padding' style={{paddingBottom: '15px'}}>
                <DataTable
                  columns={columns}
                  classNameParrant="border-left-right"
                  className="table-no-border-left-right"
                  data={stampHistoryList}
                  isGlobalFilter={true}
                  minWidth="440px"
                  globalSearchStyle={css({ marginLeft: "-1px", borderRadius: "3px" })}
                />
              </div>         
            </FormContents>
          </div>

          {/* {userRole !== '1' && 
        <div css={dataTable}>
          <FormContents customStyle={css({ borderRadius: "3px" })}>
            <div css={css({ marginLeft: "28px" })}>
              <FormLabel
                label="打刻履歴"
                customStyle={css({ fontWeight: "bold" })}
              />
              <div css={css({ marginTop: "10px" })}>
                  <Select
                  name="orgState"
                  value={String(selectedOrg)}
                  setValue={(arg: string) => { setSelectedOrg(arg); }}
                  options={orgTreesOptions}
                  width="400px"
                />
              </div>
            </div>
            <DataTable
              columns={columns}
              data={stampHistoryList}
              isGlobalFilter={true}
              globalSearchStyle={css({
                marginLeft: "28px",
                borderRadius: "3px",
              })}
            />
          </FormContents>
        </div>
      } */}

        </div>
      </BlockUI>
    </TimeRecorderContext.Provider>
  );
};

export default Recorder;
